import {
  KeyboardAvoidingView,
  Switch,
  TouchableOpacity,
  View,
} from "react-native"

import { useSafeAreaInsets } from "react-native-safe-area-context"

import BackButton from "@/assets/icons/back-arrow-dark.svg"
import { router } from "expo-router"
import {
  checkCanRequestOneSignalPermission,
  oneSignalOptIn,
  oneSignalOptOut,
  requestOneSignalPermission,
  useOneSignalPermissions,
} from "@/stores/oneSignal"
import { BFColors } from "@/constants/Colors"
import { useEffect, useState } from "react"
import * as AccountEvents from "@/analytics/events/account"
import { useViewedCallback } from "@/analytics/react"
import { setStatusBarStyle } from "@/stores/statusBarStyle"
import BFText from "@/components/standard/BFText"

const EditNotificationsForm: React.FC = () => {
  const { hasPushPermission, hasUserOptIn } = useOneSignalPermissions()
  const [canRequestPushPermission, setCanRequestPushPermission] = useState<
    boolean | undefined
  >(undefined)

  const pushDisabled = !hasPushPermission || !hasUserOptIn
  const userOptedOut = hasPushPermission && !hasUserOptIn

  const canEnablePush =
    pushDisabled && (canRequestPushPermission || userOptedOut)

  const disableNotificationToggle = pushDisabled && !canEnablePush

  const checkCanRequest = async () => {
    const canRequest = await checkCanRequestOneSignalPermission()
    setCanRequestPushPermission(canRequest)
  }

  const requestPermission = async () => {
    const result = await requestOneSignalPermission()
    if (!result) {
      void checkCanRequest()
    }
  }

  useEffect(() => {
    void checkCanRequest()
  }, [])

  return (
    <View className="flex flex-col justify-start flex-1">
      <View className="flex flex-col mt-8 shrink-0">
        <View className="flex flex-row items-center justify-between">
          <View>
            <BFText>Enable Notifications</BFText>
          </View>
          <View>
            <Switch
              trackColor={{ true: BFColors.calmBlue, false: BFColors.warmGrey }}
              value={!pushDisabled}
              onValueChange={(value) => {
                AccountEvents.notificationsToggleClicked({
                  actionTaken: value ? "enable" : "disable",
                })

                if (value && canEnablePush && !userOptedOut) {
                  void requestPermission()
                } else if (value && canEnablePush && userOptedOut) {
                  oneSignalOptIn()
                  AccountEvents.notificationsEnabled()
                }

                if (!value) {
                  oneSignalOptOut()
                  AccountEvents.notificationsDisabled()
                }
              }}
              disabled={disableNotificationToggle}
            />
          </View>
        </View>
      </View>
    </View>
  )
}

const onBack = () => {
  router.back()
}

export default function EditNotificationsScreen() {
  const insets = useSafeAreaInsets()

  useViewedCallback(() => {
    setStatusBarStyle("dark")
    AccountEvents.accountEditNotificicationsViewed()
  })

  return (
    <KeyboardAvoidingView
      className="flex flex-col justify-start flex-1 bg-white"
      behavior="padding"
      style={{
        paddingTop: insets.top,
      }}
    >
      <View className="flex flex-col px-8 shrink-0">
        <TouchableOpacity testID={"back-button"} onPress={onBack}>
          <BackButton />
        </TouchableOpacity>
        <BFText
          className={`text-[24px] font-figtree-bold leading-[24px] text-midnight-blue pt-4`}
        >
          Edit Notifications
        </BFText>
      </View>
      <View className="flex flex-col justify-between flex-1 h-full px-8 pb-8">
        <EditNotificationsForm />
      </View>
    </KeyboardAvoidingView>
  )
}
